import * as PIXI from "pixi.js";
import {
  Room,
  Avatar,
  FloorFurniture,
  WallFurniture,
  RoomCamera,
  Shroom,
  AvatarAction,
} from "@jankuss/shroom";

interface HiddenAreaData {
  id: number;
  x: number;
  y: number;
  width: number;
  height: number;
  hidden: boolean;
}

interface RoomStateData {
  roomId: number;
  model?: {
    id: string;
    heightmap: string;
    wallHeight: number;
    doorX: number;
    doorY: number;
    doorZ: number;
    doorDir: number;
    roomScale: number;
  };
  avatars: AvatarData[];
  floorItems: FloorItemData[];
  wallItems: WallItemData[];
  heightmap?: number[][];
  floorPlan?: string;
  wallColor?: string;
  floorColor?: string;
  hideWalls?: boolean;
  hiddenAreas?: HiddenAreaData[];
}

interface AvatarData {
  id: number;
  index: number;
  name: string;
  figure: string;
  type: number;
  location: { x: number; y: number; z: number };
  direction: number;
  headDirection: number;
  stance: number;
  dance: number;
  effect: number;
  movingTo?: { x: number; y: number; z: number };
}

interface FloorItemData {
  id: number;
  kind: number;
  identifier?: string;
  x: number;
  y: number;
  z: number;
  direction: number;
  state: string;
}

interface WallItemData {
  id: number;
  kind: number;
  identifier?: string;
  location: string;
  state: string;
}

interface ChatData {
  avatarIndex: number;
  avatarName: string;
  message: string;
  chatType: "talk" | "shout" | "whisper";
  bubbleStyle: number;
}

interface WiredMovement {
  type: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  fromZ: number;
  toZ: number;
  id: number;
  duration: number;
}

interface NavRoom {
  flatId: number;
  roomName: string;
  ownerName: string;
  userCount: number;
  maxUserCount: number;
  doorMode: number;
  description: string;
  score: number;
  tags: string[];
  groupName: string;
}

interface NavBlock {
  searchCode: string;
  text: string;
  rooms: NavRoom[];
}

interface NavResults {
  searchCode: string;
  filterText: string;
  blocks: NavBlock[];
}

function escapexss(str: string): string {
  const div = document.createElement("div");
  div.textContent = str;
  return div.innerHTML;
}

function removespecialchars(text: string): string {
  return text;
}

function applyvolterfont(text: string): string {
  let result = "";
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (/[a-zA-Z0-9 ]/.test(char)) {
      result += char;
    } else {
      result += `<span style="font-family:'Volter',Arial,sans-serif;">${char}</span>`;
    }
  }
  return result;
}

function parsebbcode(text: string): string {
  text = removespecialchars(text);
  let result = escapexss(text);
  result = result.replace(/\[b\](.*?)\[\/b\]/gi, "<b>$1</b>");
  result = result.replace(/\[i\](.*?)\[\/i\]/gi, "<i>$1</i>");
  result = result.replace(/\[u\](.*?)\[\/u\]/gi, "<u>$1</u>");
  result = result.replace(/\[s\](.*?)\[\/s\]/gi, "<s>$1</s>");
  result = result.replace(/\[color=([^\]]+)\](.*?)\[\/color\]/gi, '<span style="color:$1">$2</span>');
  result = applyvolterfont(result);
  return result;
}

function getbubblestyle(style: number): { bg: string; border: string; color: string } {
  const styles: Record<number, { bg: string; border: string; color: string }> = {
    0: { bg: "#ffffff", border: "#000000", color: "#000000" },
    1: { bg: "#cfcfcf", border: "#5a5a5a", color: "#000000" },
    2: { bg: "#e7e7e7", border: "#000000", color: "#000000" },
    3: { bg: "#555555", border: "#000000", color: "#ffffff" },
    4: { bg: "#ff3333", border: "#990000", color: "#ffffff" },
    5: { bg: "#3366ff", border: "#0033cc", color: "#ffffff" },
    6: { bg: "#ffff00", border: "#999900", color: "#000000" },
    7: { bg: "#00ff00", border: "#009900", color: "#000000" },
    8: { bg: "#333333", border: "#ffffff", color: "#ffffff" },
    9: { bg: "#ccccff", border: "#6666aa", color: "#000066" },
    10: { bg: "#ffcccc", border: "#aa6666", color: "#660000" },
    11: { bg: "#ffffcc", border: "#aaaa66", color: "#666600" },
    12: { bg: "#ccffcc", border: "#66aa66", color: "#006600" },
    13: { bg: "#ccffff", border: "#66aaaa", color: "#006666" },
    14: { bg: "#ffccff", border: "#aa66aa", color: "#660066" },
    23: { bg: "#1e8ba3", border: "#0d3d47", color: "#ffffff" },
    24: { bg: "#b87f33", border: "#5c3f19", color: "#ffffff" },
    25: { bg: "#7c3d7c", border: "#3e1e3e", color: "#ffffff" },
    26: { bg: "#3d7c3d", border: "#1e3e1e", color: "#ffffff" },
    27: { bg: "#7c3d3d", border: "#3e1e1e", color: "#ffffff" },
    29: { bg: "#5c4033", border: "#2e201a", color: "#ffffff" },
    30: { bg: "#9b2a2a", border: "#4d1515", color: "#ffffff" },
    31: { bg: "#e8e8e8", border: "#b4b4b4", color: "#333333" },
    32: { bg: "#a4d8e4", border: "#6ba8b8", color: "#000000" },
    33: { bg: "#ff9900", border: "#cc6600", color: "#000000" },
    34: { bg: "#5599ff", border: "#2266cc", color: "#ffffff" },
    35: { bg: "#3e9e49", border: "#1f4f24", color: "#ffffff" },
    36: { bg: "#e8a8c8", border: "#b87898", color: "#000000" },
    37: { bg: "#e0c89b", border: "#a08050", color: "#000000" },
    38: { bg: "#6699cc", border: "#336699", color: "#ffffff" },
    39: { bg: "#ff6b6b", border: "#cc3333", color: "#ffffff" },
    40: { bg: "#fff4d9", border: "#d4b26a", color: "#000000" },
    41: { bg: "#b2e0a8", border: "#6db85c", color: "#000000" },
    42: { bg: "#e2d6f2", border: "#9a86b8", color: "#000000" },
    43: { bg: "#d4f4f4", border: "#8ac8c8", color: "#000000" },
    44: { bg: "#2c2c2c", border: "#0c0c0c", color: "#ffffff" },
    212: { bg: "#ff6600", border: "#cc3300", color: "#ffffff" },
    1013: { bg: "#ffffff", border: "#000000", color: "#000000" },
  };
  return styles[style] || styles[0];
}

const view = document.querySelector("#root") as HTMLCanvasElement | undefined;
const container = document.querySelector("#container") as HTMLDivElement | undefined;
if (view == null || container == null) throw new Error("Invalid view");

const application = new PIXI.Application({
  view,
  antialias: false,
  resolution: window.devicePixelRatio,
  autoDensity: true,
  width: window.innerWidth,
  height: window.innerHeight,
  backgroundColor: 0x000000,
  resizeTo: window,
});

PIXI.settings.SCALE_MODE = PIXI.SCALE_MODES.NEAREST;

const shroom = Shroom.create({
  application,
  resourcePath: "./resources",
});

let room: Room | null = null;
let roomCamera: RoomCamera | null = null;
let currentRoomId: number | null = null;
let ws: WebSocket | null = null;
const avatars = new Map<number, Avatar>();
const floorItems = new Map<number, FloorFurniture>();
const wallItems = new Map<number, WallFurniture>();
const hiddenAreas = new Map<number, HiddenAreaData>();
let currentFloorPlan: string = "";
let wallsHidden = false;
let rebuildpending = false;
let rebuilding = false;
let laststate: RoomStateData | null = null;

const chatlog: ChatData[] = [];
let chatlogopen = false;

const scrollbarhide = `scrollbar-width:none;-ms-overflow-style:none;`;
const scrollbarhidestyle = document.createElement("style");
scrollbarhidestyle.textContent = `#nav-list::-webkit-scrollbar, #chat-log::-webkit-scrollbar { display:none; }`;
document.head.appendChild(scrollbarhidestyle);

const chatcontainer = document.createElement("div");
chatcontainer.id = "chat-bubbles";
chatcontainer.style.cssText = "position:fixed;top:0;left:0;right:0;bottom:0;pointer-events:none;overflow:hidden;z-index:1000;";
document.body.appendChild(chatcontainer);

const chatlogpanel = document.createElement("div");
chatlogpanel.id = "chat-log";
chatlogpanel.style.cssText = "position:fixed;left:0;top:0;bottom:0;width:220px;background:rgba(20,20,22,0.95);color:#fff;font-family:Arial,sans-serif;font-size:11px;overflow-y:auto;transform:translateX(-100%);transition:transform 0.2s;z-index:1001;padding:8px;box-sizing:border-box;" + scrollbarhide;
document.body.appendChild(chatlogpanel);

const chatlogtoggle = document.createElement("button");
chatlogtoggle.textContent = "Chat";
chatlogtoggle.style.cssText = "position:fixed;left:10px;bottom:10px;padding:8px 16px;background:#333;color:#fff;border:none;border-radius:4px;cursor:pointer;z-index:1002;font-size:12px;";
chatlogtoggle.onclick = () => {
  chatlogopen = !chatlogopen;
  chatlogpanel.style.transform = chatlogopen ? "translateX(0)" : "translateX(-100%)";
};
document.body.appendChild(chatlogtoggle);

let navopen = false;
let navsearchcode = "hotel_view";

const navpanel = document.createElement("div");
navpanel.id = "nav-panel";
navpanel.style.cssText = `
  position:fixed;right:0;top:0;bottom:0;width:300px;
  background:#1a1a1f;color:#ccc;font-family:Arial,sans-serif;font-size:12px;
  transform:translateX(100%);transition:transform 0.2s;z-index:1001;
  display:flex;flex-direction:column;border-left:1px solid #333;
`;
document.body.appendChild(navpanel);

const navheader = document.createElement("div");
navheader.style.cssText = "padding:12px;background:#151518;border-bottom:1px solid #333;";
navheader.innerHTML = `
  <div style="font-size:13px;font-weight:600;margin-bottom:10px;color:#fff;">Navigator</div>
  <div style="display:flex;gap:4px;margin-bottom:10px;">
    <button data-code="hotel_view" class="nav-tab active" style="flex:1;padding:6px 10px;background:#2a4a6a;border:none;color:#fff;cursor:pointer;font-size:11px;border-radius:3px;">Rooms</button>
    <button data-code="official_view" class="nav-tab" style="flex:1;padding:6px 10px;background:#252528;border:none;color:#888;cursor:pointer;font-size:11px;border-radius:3px;">Public</button>
  </div>
  <input type="text" id="nav-search" placeholder="Search..." style="width:100%;padding:8px 10px;background:#222;border:1px solid #333;color:#fff;font-size:12px;border-radius:3px;box-sizing:border-box;">
`;
navpanel.appendChild(navheader);

const navlist = document.createElement("div");
navlist.id = "nav-list";
navlist.style.cssText = "flex:1;overflow-y:auto;padding:8px;" + scrollbarhide;
navpanel.appendChild(navlist);

const navtoggle = document.createElement("button");
navtoggle.textContent = "Navigator";
navtoggle.style.cssText = "position:fixed;right:10px;bottom:10px;padding:8px 14px;background:#2a4a6a;color:#fff;border:none;border-radius:3px;cursor:pointer;z-index:1002;font-size:11px;";
navtoggle.onclick = () => togglenav();
document.body.appendChild(navtoggle);

function togglenav() {
  navopen = !navopen;
  navpanel.style.transform = navopen ? "translateX(0)" : "translateX(100%)";
  if (navopen && ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: "navinit", data: {} }));
    navsearch(navsearchcode, "");
  }
}

function navsearch(code: string, filter: string) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    navsearchcode = code;
    ws.send(JSON.stringify({ type: "navsearch", data: { searchCode: code, filter } }));
    navlist.innerHTML = '<div style="color:#888;padding:20px;text-align:center;">Searching...</div>';
  }
}

navheader.querySelectorAll("button[data-code]").forEach((btn) => {
  btn.addEventListener("click", () => {
    const code = btn.getAttribute("data-code") || "hotel_view";
    navsearchcode = code;
    navheader.querySelectorAll("button[data-code]").forEach((b) => {
      (b as HTMLElement).style.background = "#252532";
      (b as HTMLElement).style.color = "#aaa";
    });
    (btn as HTMLElement).style.background = "#2d5a8a";
    (btn as HTMLElement).style.color = "#fff";
    const input = document.getElementById("nav-search") as HTMLInputElement;
    navsearch(code, input?.value || "");
  });
});

document.getElementById("nav-search-btn")?.addEventListener("click", () => {
  const input = document.getElementById("nav-search") as HTMLInputElement;
  navsearch(navsearchcode, input?.value || "");
});

document.getElementById("nav-search")?.addEventListener("keydown", (e) => {
  if (e.key === "Enter") {
    const input = e.target as HTMLInputElement;
    navsearch(navsearchcode, input?.value || "");
  }
});

function parsecategorytext(text: string): string {
  if (!text) return "";
  if (text.startsWith("${") && text.endsWith("}")) {
    const key = text.slice(2, -1);
    const parts = key.split(".");
    const last = parts[parts.length - 1];
    return last.replace(/_/g, " ").toLowerCase().replace(/^\w/, c => c.toUpperCase());
  }
  return text;
}

function rendernavresults(results: NavResults) {
  navlist.innerHTML = "";

  if (results.blocks.length === 0) {
    navlist.innerHTML = '<div style="color:#666;padding:30px;text-align:center;font-size:12px;">No rooms found</div>';
    return;
  }

  for (const block of results.blocks) {
    if (block.rooms.length === 0) continue;

    const blockel = document.createElement("div");
    blockel.style.cssText = "margin-bottom:12px;";

    const categorytext = parsecategorytext(block.text);
    if (categorytext) {
      const title = document.createElement("div");
      title.style.cssText = "font-size:10px;color:#666;margin-bottom:6px;font-weight:600;text-transform:uppercase;";
      title.textContent = categorytext;
      blockel.appendChild(title);
    }

    for (const room of block.rooms) {
      const roomel = document.createElement("div");
      roomel.style.cssText = `background:#222;padding:8px 10px;margin-bottom:4px;border-radius:3px;cursor:pointer;`;
      roomel.onmouseenter = () => { roomel.style.background = "#2a2a2a"; };
      roomel.onmouseleave = () => { roomel.style.background = "#222"; };
      roomel.onclick = () => {
        gotoroom(room.flatId);
        togglenav();
      };

      const nameline = document.createElement("div");
      nameline.style.cssText = "font-weight:600;margin-bottom:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:#ddd;font-size:12px;";
      nameline.innerHTML = applyvolterfont(escapexss(removespecialchars(room.roomName)));

      const ownerline = document.createElement("div");
      ownerline.style.cssText = "font-size:10px;color:#777;margin-bottom:4px;";
      ownerline.innerHTML = applyvolterfont(escapexss(removespecialchars(room.ownerName)));

      const infoline = document.createElement("div");
      infoline.style.cssText = "font-size:10px;color:#666;display:flex;gap:10px;";
      infoline.innerHTML = `
        <span>${room.userCount}/${room.maxUserCount}</span>
        <span style="color:#997700;">${room.score}</span>
        ${room.groupName ? `<span>${applyvolterfont(escapexss(removespecialchars(room.groupName)))}</span>` : ""}
      `;

      roomel.appendChild(nameline);
      roomel.appendChild(ownerline);

      if (room.description) {
        const desc = document.createElement("div");
        desc.style.cssText = "font-size:10px;color:#555;margin-bottom:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;";
        desc.innerHTML = applyvolterfont(escapexss(removespecialchars(room.description)));
        roomel.appendChild(desc);
      }

      roomel.appendChild(infoline);
      blockel.appendChild(roomel);
    }

    navlist.appendChild(blockel);
  }
}

(window as any).nav = togglenav;
(window as any).navsearch = (filter: string) => navsearch(navsearchcode, filter);

function sendwalk(x: number, y: number) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: "walk", data: { x, y } }));
  }
}

function gotoroom(roomid: number) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: "goto", data: { roomId: roomid } }));
    console.log(`[shroom] navigating to room ${roomid}...`);
  } else {
    console.log("[shroom] not connected");
  }
}

function loginwithsso(token: string, hotel = "us", version?: string) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: "sso", data: { token, hotel, version } }));
    console.log(`[shroom] logging in with SSO token (${hotel}${version ? `, version: ${version}` : ""})...`);
  } else {
    console.log("[shroom] not connected");
  }
}

(window as any).goto = gotoroom;
(window as any).sso = loginwithsso;

function clearroom() {
  currentRoomId = null;

  if (roomCamera) {
    try {
      roomCamera.destroy();
      application.stage.removeChild(roomCamera);
    } catch {}
    roomCamera = null;
  }

  if (room) {
    for (const av of avatars.values()) {
      try { room.removeRoomObject(av); } catch {}
    }
    for (const item of floorItems.values()) {
      try { room.removeRoomObject(item); } catch {}
    }
    for (const item of wallItems.values()) {
      try { room.removeRoomObject(item); } catch {}
    }
    try { room.destroy(); } catch {}
    room = null;
  }

  avatars.clear();
  floorItems.clear();
  wallItems.clear();
}

function convertfromheightmap(heightmap: number[][]): string {
  const result: string[] = [];
  for (const row of heightmap) {
    let line = "";
    for (const val of row) {
      if (val === -1) {
        line += "x";
      } else if (val < 0) {
        line += "0";
      } else {
        const height = Math.floor(val / 256);
        line += height.toString(36);
      }
    }
    result.push(line);
  }
  return result.join("\n");
}

function convertfloorplan(model: string): string {
  const lines = model.split(/[\r\n]+/).filter((l) => l.length > 0);

  let maxlen = 0;
  for (const line of lines) {
    if (line.length > maxlen) maxlen = line.length;
  }

  const grid: string[][] = [];

  for (const line of lines) {
    const row: string[] = [];
    for (const c of line) {
      if (c === "x" || c === "X") {
        row.push("x");
      } else {
        row.push(c.toLowerCase());
      }
    }
    while (row.length < maxlen) {
      row.push("x");
    }
    grid.push(row);
  }

  for (const area of hiddenAreas.values()) {
    for (let dy = 0; dy < area.height; dy++) {
      for (let dx = 0; dx < area.width; dx++) {
        const gy = area.y + dy;
        const gx = area.x + dx;
        if (gy >= 0 && gy < grid.length && gx >= 0 && gx < grid[gy].length) {
          grid[gy][gx] = "x";
        }
      }
    }
  }

  return grid.map((row) => row.join("")).join("\n");
}

function coloridtohex(id: string): string {
  const num = parseInt(id, 10);
  if (isNaN(num)) return "#888888";
  const hex = num.toString(16).padStart(6, "0");
  return `#${hex}`;
}

function loadroom(state: RoomStateData) {
  try {
    clearroom();
    currentRoomId = state.roomId;
    laststate = state;

    hiddenAreas.clear();
    if (state.hiddenAreas) {
      for (const area of state.hiddenAreas) {
        if (area.hidden) {
          hiddenAreas.set(area.id, area);
        }
      }
    }
    console.log(`[client] loadroom: ${hiddenAreas.size} hidden areas applied`);

    wallsHidden = state.hideWalls ?? false;

    let tilemap: string;
    if (state.floorPlan) {
      currentFloorPlan = state.floorPlan;
      tilemap = convertfloorplan(state.floorPlan);
      console.log(`[client] tilemap after hidden areas:\n${tilemap.substring(0, 500)}...`);
    } else if (state.heightmap && state.heightmap.length > 0) {
      currentFloorPlan = "";
      tilemap = convertfromheightmap(state.heightmap);
    } else {
      return;
    }

    room = Room.create(shroom, { tilemap });
    room.x = 100;
    room.y = 100;
    (window as any).room = room;

    if (state.model) {
      room.roomScale = state.model.roomScale ?? 64;
      room.entryX = state.model.doorX ?? -1;
      room.entryY = state.model.doorY ?? -1;
      room.floorWallHeight = state.model.wallHeight ?? -1;
      if (state.model.wallHeight > 0) {
        room.wallHeight = state.model.wallHeight * 32;
      }
      console.log(`[room] scale=${room.roomScale} entry=${room.entryX},${room.entryY} floorWallH=${room.floorWallHeight} visualWallH=${room.wallHeight}`);
    }

    if (wallsHidden) {
      room.hideWalls = true;
    }

    room.onTileClick = (pos) => {
      sendwalk(pos.roomX, pos.roomY);
    };

    if (state.wallColor) {
      room.wallColor = coloridtohex(state.wallColor);
    }
    if (state.floorColor) {
      room.floorColor = coloridtohex(state.floorColor);
    }

    roomCamera = RoomCamera.forScreen(room);
    application.stage.addChild(roomCamera);

    for (const avatardata of state.avatars) {
      addavatar(avatardata);
    }

    for (const itemdata of state.floorItems) {
      addflooritem(itemdata);
    }

    for (const itemdata of state.wallItems) {
      addwallitem(itemdata);
    }
  } catch (err) {
    console.error("[shroom] loadroom error:", err);
  }
}

function addavatar(data: AvatarData) {
  if (!room || avatars.has(data.index)) return;

  if (data.type === 2) return;

  try {
    const avatar = new Avatar({
      look: data.figure,
      direction: data.direction,
      roomX: data.location.x,
      roomY: data.location.y,
      roomZ: data.location.z,
    });

    if (data.effect) {
      avatar.effect = data.effect.toString();
    }

    room.addRoomObject(avatar);
    avatars.set(data.index, avatar);
  } catch {}
}

function updateavatars(updates: Partial<AvatarData>[]) {
  for (const update of updates) {
    if (update.index === undefined) continue;
    const avatar = avatars.get(update.index);
    if (!avatar) continue;

    if (update.location) {
      if (update.movingTo) {
        avatar.roomX = update.location.x;
        avatar.roomY = update.location.y;
        avatar.roomZ = update.location.z;
        avatar.walk(update.movingTo.x, update.movingTo.y, update.movingTo.z, {
          direction: update.direction,
        });
      } else {
        avatar.roomX = update.location.x;
        avatar.roomY = update.location.y;
        avatar.roomZ = update.location.z;
        if (update.direction !== undefined) {
          avatar.direction = update.direction;
        }
      }
    } else if (update.direction !== undefined) {
      avatar.direction = update.direction;
    }

    if (update.stance !== undefined) {
      avatar.removeAction(AvatarAction.Sit);
      avatar.removeAction(AvatarAction.Lay);
      if (update.stance === 1) {
        avatar.addAction(AvatarAction.Sit);
      } else if (update.stance === 2) {
        avatar.addAction(AvatarAction.Lay);
      }
    }
  }
}

function removeavatar(index: number) {
  const avatar = avatars.get(index);
  if (avatar && room) {
    room.removeRoomObject(avatar);
    avatars.delete(index);
  }
}

function setavatardance(index: number, dance: number) {
  const avatar = avatars.get(index);
  if (avatar) {
    if (dance > 0) {
      avatar.addAction(AvatarAction.Dance);
    } else {
      avatar.removeAction(AvatarAction.Dance);
    }
  }
}

function setavatareffect(index: number, effect: number) {
  const avatar = avatars.get(index);
  if (avatar) {
    avatar.effect = effect > 0 ? effect.toString() : undefined;
  }
}

function setavataraction(index: number, action: number) {
  const avatar = avatars.get(index);
  if (!avatar) return;

  avatar.removeAction(AvatarAction.Wave);

  if (action === 1) {
    avatar.addAction(AvatarAction.Wave);
  }
}

function addflooritem(data: FloorItemData) {
  if (!room || floorItems.has(data.id)) return;

  const opts: any = {
    roomX: data.x,
    roomY: data.y,
    roomZ: data.z,
    direction: data.direction,
    animation: data.state,
  };

  if (data.identifier) {
    opts.type = data.identifier;
  } else {
    opts.id = data.kind;
  }

  const furni = new FloorFurniture(opts);

  furni.onClick = () => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "clickfurni", data: { id: data.id, category: 0 } }));
    }
  };

  furni.onDoubleClick = () => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "usefurni", data: { id: data.id, category: 0 } }));
    }
  };

  room.addRoomObject(furni);
  floorItems.set(data.id, furni);
}

function updateflooritem(data: FloorItemData) {
  const item = floorItems.get(data.id);
  if (!item) return;

  try {
    item.roomX = data.x;
    item.roomY = data.y;
    item.roomZ = data.z;
    item.direction = data.direction;
    item.animation = data.state;
  } catch {
  }
}

function removeflooritem(id: number) {
  const item = floorItems.get(id);
  if (item && room) {
    room.removeRoomObject(item);
    floorItems.delete(id);
  }
}

function parsewallocation(loc: string): { roomX: number; roomY: number; offsetX: number; offsetY: number; direction: number } | null {
  const match = loc.match(/:w=(\d+),(\d+)\s+l=(\d+),(\d+)\s+([lr])/);
  if (!match) return null;
  return {
    roomX: parseInt(match[1], 10),
    roomY: parseInt(match[2], 10),
    offsetX: parseInt(match[3], 10),
    offsetY: parseInt(match[4], 10),
    direction: match[5] === "l" ? 2 : 4,
  };
}

function addwallitem(data: WallItemData) {
  if (!room || wallItems.has(data.id)) return;

  const parsed = parsewallocation(data.location);
  if (!parsed) return;

  const opts: any = {
    roomX: parsed.roomX,
    roomY: parsed.roomY,
    offsetX: parsed.offsetX,
    offsetY: parsed.offsetY,
    direction: parsed.direction,
    animation: data.state,
  };

  if (data.identifier) {
    opts.type = data.identifier;
  } else {
    opts.id = data.kind;
  }

  try {
    const furni = new WallFurniture(opts);

    furni.onClick = () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "clickfurni", data: { id: data.id, category: 0 } }));
      }
    };

    furni.onDoubleClick = () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "usefurni", data: { id: data.id, category: 0 } }));
      }
    };

    room.addRoomObject(furni);
    wallItems.set(data.id, furni);
  } catch {}
}

function updatewallitem(data: WallItemData) {
  const item = wallItems.get(data.id);
  if (!item) return;

  const parsed = parsewallocation(data.location);
  if (!parsed) return;

  try {
    item.roomX = parsed.roomX;
    item.roomY = parsed.roomY;
    item.offsetX = parsed.offsetX;
    item.offsetY = parsed.offsetY;
    item.direction = parsed.direction;
    item.animation = data.state;
  } catch {}
}

function removewallitem(id: number) {
  const item = wallItems.get(id);
  if (item && room) {
    room.removeRoomObject(item);
    wallItems.delete(id);
  }
}

function animatefurni(item: FloorFurniture, toX: number, toY: number, toZ: number, duration: number) {
  const fromX = item.roomX;
  const fromY = item.roomY;
  const fromZ = item.roomZ;
  const start = performance.now();

  function step(now: number) {
    const elapsed = now - start;
    const t = Math.min(elapsed / duration, 1);
    item.roomX = fromX + (toX - fromX) * t;
    item.roomY = fromY + (toY - fromY) * t;
    item.roomZ = fromZ + (toZ - fromZ) * t;
    if (t < 1) {
      requestAnimationFrame(step);
    }
  }
  requestAnimationFrame(step);
}

function handleslide(data: {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  items: { id: number; fromZ: number; toZ: number }[];
  avatarSlide?: { index: number; fromZ: number; toZ: number };
}) {
  for (const itemslide of data.items) {
    const item = floorItems.get(itemslide.id);
    if (item) {
      item.roomX = data.fromX;
      item.roomY = data.fromY;
      item.roomZ = itemslide.fromZ;
      animatefurni(item, data.toX, data.toY, itemslide.toZ, 500);
    }
  }

  if (data.avatarSlide) {
    const avatar = avatars.get(data.avatarSlide.index);
    if (avatar) {
      avatar.roomX = data.fromX;
      avatar.roomY = data.fromY;
      avatar.roomZ = data.avatarSlide.fromZ;
      avatar.move(data.toX, data.toY, data.avatarSlide.toZ);
    }
  }
}

function handlewiredmove(data: { movements: WiredMovement[] }) {
  for (const m of data.movements) {
    if (m.type === 1 || m.type === 2) {
      const item = floorItems.get(m.id);
      if (item) {
        item.roomX = m.fromX;
        item.roomY = m.fromY;
        item.roomZ = m.fromZ;
        animatefurni(item, m.toX, m.toY, m.toZ, m.duration > 0 ? m.duration : 500);
      }
    } else if (m.type === 3 || m.type === 4) {
      const avatar = avatars.get(m.id);
      if (avatar) {
        avatar.roomX = m.fromX;
        avatar.roomY = m.fromY;
        avatar.roomZ = m.fromZ;
        avatar.move(m.toX, m.toY, m.toZ);
      }
    }
  }
}

function handleproperty(type: string, value: string) {
  if (!room) return;
  const hex = coloridtohex(value);
  switch (type) {
    case "floor":
      room.floorColor = hex;
      break;
    case "wallpaper":
      room.wallColor = hex;
      break;
  }
}

function handleareahide(data: HiddenAreaData) {
  if (!room || !currentFloorPlan) return;

  if (data.hidden) {
    hiddenAreas.set(data.id, data);
  } else {
    hiddenAreas.delete(data.id);
  }
  rebuildroom();
}

function handlewallstoggle(hidden: boolean) {
  wallsHidden = hidden;
  if (room) {
    room.hideWalls = hidden;
  }
}

function dorebuild() {
  if (!room || !currentFloorPlan) {
    rebuilding = false;
    return;
  }

  try {
    const avatardata: { index: number; look: string; x: number; y: number; z: number; dir: number; effect?: string }[] = [];
    for (const [index, av] of avatars.entries()) {
      try {
        avatardata.push({
          index,
          look: av.look || "",
          x: av.roomX,
          y: av.roomY,
          z: av.roomZ,
          dir: av.direction,
          effect: av.effect,
        });
      } catch {}
    }

    const floordata: { id: number; type?: string; kind?: number; x: number; y: number; z: number; dir: number; anim?: string }[] = [];
    for (const [id, item] of floorItems.entries()) {
      try {
        floordata.push({
          id,
          type: (item as any)._type,
          kind: (item as any)._id,
          x: item.roomX,
          y: item.roomY,
          z: item.roomZ,
          dir: item.direction,
          anim: item.animation,
        });
      } catch {}
    }

    const walldata: { id: number; type?: string; kind?: number; x: number; y: number; ox: number; oy: number; dir: number; anim?: string }[] = [];
    for (const [id, item] of wallItems.entries()) {
      try {
        walldata.push({
          id,
          type: (item as any)._type,
          kind: (item as any)._id,
          x: item.roomX,
          y: item.roomY,
          ox: item.offsetX,
          oy: item.offsetY,
          dir: item.direction,
          anim: item.animation,
        });
      } catch {}
    }

    const savedwallcolor = room.wallColor;
    const savedfloorcolor = room.floorColor;
    const savedroomscale = room.roomScale;
    const savedentryx = room.entryX;
    const savedentryy = room.entryY;
    const savedwallheight = room.wallHeight;
    const savedfloorwallheight = room.floorWallHeight;

    if (roomCamera) {
      try {
        application.stage.removeChild(roomCamera);
        roomCamera.destroy();
      } catch {}
      roomCamera = null;
    }

    for (const av of avatars.values()) {
      try { room.removeRoomObject(av); } catch {}
    }
    for (const item of floorItems.values()) {
      try { room.removeRoomObject(item); } catch {}
    }
    for (const item of wallItems.values()) {
      try { room.removeRoomObject(item); } catch {}
    }

    try { room.destroy(); } catch {}

    avatars.clear();
    floorItems.clear();
    wallItems.clear();

    const tilemap = convertfloorplan(currentFloorPlan);
    room = Room.create(shroom, { tilemap });
    room.x = 100;
    room.y = 100;
    (window as any).room = room;

    room.roomScale = savedroomscale;
    room.entryX = savedentryx;
    room.entryY = savedentryy;
    room.floorWallHeight = savedfloorwallheight;
    if (savedwallheight > 0) room.wallHeight = savedwallheight;

    if (wallsHidden) {
      room.hideWalls = true;
    }

    room.onTileClick = (pos) => {
      sendwalk(pos.roomX, pos.roomY);
    };

    if (savedwallcolor) room.wallColor = savedwallcolor;
    if (savedfloorcolor) room.floorColor = savedfloorcolor;

    roomCamera = RoomCamera.forScreen(room);
    application.stage.addChild(roomCamera);

    for (const data of avatardata) {
      if (!data.look) continue;
      try {
        const av = new Avatar({
          look: data.look,
          direction: data.dir,
          roomX: data.x,
          roomY: data.y,
          roomZ: data.z,
        });
        if (data.effect) av.effect = data.effect;
        room.addRoomObject(av);
        avatars.set(data.index, av);
      } catch {}
    }

    for (const data of floordata) {
      try {
        const opts: any = {
          roomX: data.x,
          roomY: data.y,
          roomZ: data.z,
          direction: data.dir,
          animation: data.anim,
        };
        if (data.type) opts.type = data.type;
        else if (data.kind) opts.id = data.kind;
        const item = new FloorFurniture(opts);
        room.addRoomObject(item);
        floorItems.set(data.id, item);
      } catch {}
    }

    for (const data of walldata) {
      try {
        const opts: any = {
          roomX: data.x,
          roomY: data.y,
          offsetX: data.ox,
          offsetY: data.oy,
          direction: data.dir,
          animation: data.anim,
        };
        if (data.type) opts.type = data.type;
        else if (data.kind) opts.id = data.kind;
        const item = new WallFurniture(opts);
        room.addRoomObject(item);
        wallItems.set(data.id, item);
      } catch {}
    }
  } catch (err) {
    console.error("[shroom] rebuild error:", err);
  }

  rebuilding = false;

  if (rebuildpending) {
    rebuildpending = false;
    requestAnimationFrame(() => rebuildroom());
  }
}

function rebuildroom() {
  if (rebuilding) {
    rebuildpending = true;
    return;
  }
  rebuilding = true;
  requestAnimationFrame(() => dorebuild());
}

const activebubbles: Map<number, HTMLElement[]> = new Map();

function renderchatlog() {
  chatlogpanel.innerHTML = "";
  const header = document.createElement("div");
  header.style.cssText = "font-weight:bold;font-size:12px;margin-bottom:8px;padding-bottom:6px;border-bottom:1px solid #333;color:#888;";
  header.textContent = "CHAT LOG";
  chatlogpanel.appendChild(header);

  for (let i = chatlog.length - 1; i >= 0; i--) {
    const entry = chatlog[i];
    const style = getbubblestyle(entry.bubbleStyle);
    const line = document.createElement("div");
    line.style.cssText = `margin-bottom:2px;padding:3px 5px;font-size:11px;border-radius:2px;background:rgba(255,255,255,0.05);`;

    const name = document.createElement("span");
    name.style.cssText = `font-weight:bold;color:#aaa;margin-right:4px;`;
    name.innerHTML = applyvolterfont(escapexss(removespecialchars(entry.avatarName))) + ":";

    const msg = document.createElement("span");
    msg.style.color = "#ddd";
    msg.innerHTML = parsebbcode(entry.message);

    line.appendChild(name);
    line.appendChild(msg);

    if (entry.chatType === "whisper") {
      line.style.fontStyle = "italic";
      line.style.opacity = "0.7";
    } else if (entry.chatType === "shout") {
      name.style.color = "#ffcc00";
    }

    chatlogpanel.appendChild(line);
  }
}

function getavatarscreenpos(index: number): { x: number; y: number } | null {
  const avatar = avatars.get(index);
  if (!avatar) return null;
  const pos = avatar.screenPosition;
  if (!pos) return null;
  const canvasrect = view?.getBoundingClientRect();
  if (!canvasrect) return null;
  return {
    x: canvasrect.left + pos.x + 32,
    y: canvasrect.top + pos.y - 100
  };
}

function updatebubblepositions() {
  activebubbles.forEach((bubbles, index) => {
    const pos = getavatarscreenpos(index);
    if (!pos) return;
    let yoffset = 0;
    for (const bubble of bubbles) {
      bubble.style.left = pos.x + "px";
      bubble.style.top = (pos.y - yoffset) + "px";
      yoffset += bubble.offsetHeight + 4;
    }
  });
  requestAnimationFrame(updatebubblepositions);
}
requestAnimationFrame(updatebubblepositions);

function showchatbubble(data: ChatData) {
  chatlog.push(data);
  if (chatlog.length > 200) chatlog.shift();
  renderchatlog();

  const style = getbubblestyle(data.bubbleStyle);
  const pos = getavatarscreenpos(data.avatarIndex);

  const bubble = document.createElement("div");
  bubble.style.cssText = `
    position:fixed;
    left:${pos ? pos.x : window.innerWidth / 2}px;
    top:${pos ? pos.y : 100}px;
    transform:translateX(-50%);
    background:${style.bg};
    color:${style.color};
    padding:4px 8px;
    border-radius:4px;
    font-family:Arial,sans-serif;
    font-size:11px;
    max-width:220px;
    white-space:nowrap;
    overflow:hidden;
    text-overflow:ellipsis;
    opacity:0;
    transition:opacity 0.15s;
    pointer-events:none;
    box-shadow:0 1px 3px rgba(0,0,0,0.3);
    z-index:1000;
  `;

  if (data.chatType === "shout") {
    bubble.style.fontWeight = "bold";
    bubble.style.maxWidth = "280px";
  } else if (data.chatType === "whisper") {
    bubble.style.fontStyle = "italic";
  }

  const namespan = document.createElement("span");
  namespan.style.cssText = "font-weight:bold;margin-right:4px;";
  namespan.innerHTML = applyvolterfont(escapexss(removespecialchars(data.avatarName))) + ":";

  const msgspan = document.createElement("span");
  msgspan.innerHTML = parsebbcode(data.message);

  bubble.appendChild(namespan);
  bubble.appendChild(msgspan);
  chatcontainer.appendChild(bubble);

  if (!activebubbles.has(data.avatarIndex)) {
    activebubbles.set(data.avatarIndex, []);
  }
  activebubbles.get(data.avatarIndex)!.unshift(bubble);

  requestAnimationFrame(() => {
    bubble.style.opacity = "1";
  });

  setTimeout(() => {
    bubble.style.opacity = "0";
  }, 5000);

  setTimeout(() => {
    if (bubble.parentNode) bubble.parentNode.removeChild(bubble);
    const arr = activebubbles.get(data.avatarIndex);
    if (arr) {
      const idx = arr.indexOf(bubble);
      if (idx > -1) arr.splice(idx, 1);
      if (arr.length === 0) activebubbles.delete(data.avatarIndex);
    }
  }, 5200);
}

function handlemessage(type: string, data: unknown) {
  try {
    switch (type) {
      case "room:state":
        loadroom(data as RoomStateData);
        break;
      case "room:leave":
        clearroom();
        break;
      case "avatars:add":
        for (const av of data as AvatarData[]) {
          addavatar(av);
        }
        break;
      case "avatars:update":
        updateavatars(data as Partial<AvatarData>[]);
        break;
      case "avatar:remove":
        removeavatar((data as { index: number }).index);
        break;
      case "avatar:dance":
        setavatardance(
          (data as { index: number; dance: number }).index,
          (data as { index: number; dance: number }).dance
        );
        break;
      case "avatar:effect":
        setavatareffect(
          (data as { index: number; effect: number }).index,
          (data as { index: number; effect: number }).effect
        );
        break;
      case "avatar:action":
        setavataraction(
          (data as { index: number; action: number }).index,
          (data as { index: number; action: number }).action
        );
        break;
      case "floor:add":
        for (const item of data as FloorItemData[]) {
          addflooritem(item);
        }
        break;
      case "floor:update":
        updateflooritem(data as FloorItemData);
        break;
      case "floor:remove":
        removeflooritem((data as { id: number }).id);
        break;
      case "wall:add":
        for (const item of data as WallItemData[]) {
          addwallitem(item);
        }
        break;
      case "wall:update":
        updatewallitem(data as WallItemData);
        break;
      case "wall:remove":
        removewallitem((data as { id: number }).id);
        break;
      case "slide":
        handleslide(
          data as {
            fromX: number;
            fromY: number;
            toX: number;
            toY: number;
            items: { id: number; fromZ: number; toZ: number }[];
            avatarSlide?: { index: number; fromZ: number; toZ: number };
          }
        );
        break;
      case "property":
        handleproperty(
          (data as { type: string; value: string }).type,
          (data as { type: string; value: string }).value
        );
        break;
      case "chat":
        showchatbubble(data as ChatData);
        break;
      case "area:hide":
        handleareahide(data as HiddenAreaData);
        break;
      case "walls:toggle":
        handlewallstoggle((data as { hidden: boolean }).hidden);
        break;
      case "wiredmove":
        handlewiredmove(data as { movements: WiredMovement[] });
        break;
      case "nav:results":
        rendernavresults(data as NavResults);
        break;
    }
  } catch (err) {
    console.error("[shroom] message error:", type, err);
  }
}

function getwsurl(): string {
  const islocal = location.hostname === "localhost" || location.hostname === "127.0.0.1";
  if (islocal) {
    return "ws://localhost:8888";
  }
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/ws`;
}

function connect() {
  ws = new WebSocket(getwsurl());

  ws.onopen = () => {
  };

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data);
      handlemessage(msg.type, msg.data);
    } catch (err) {}
  };

  ws.onclose = () => {
    ws = null;
    setTimeout(connect, 3000);
  };

  ws.onerror = () => {
  };
}

connect();
